import { useState, useEffect } from 'react';
import { GOLD, EMBER, MONO } from '../lib/constants';
import { Mo, Bd, Dp } from './shared';
import { CATEGORY_LABELS } from './InsightsShared';
import storage from '../lib/storage';

const API = process.env.REACT_APP_BACKEND_URL;

/* ══════════════════════════════════════════════
   QUICK FIX - Swipe Card Interface for 
   Transaction Categorization
   ══════════════════════════════════════════════ */

// Classification options for the full category picker
const CATEGORY_OPTIONS = [
  { id: 'food_drink', label: 'Food & Drink', icon: '🍔', category: 'FOOD_AND_DRINK' },
  { id: 'entertainment', label: 'Entertainment', icon: '🎬', category: 'ENTERTAINMENT' },
  { id: 'shopping', label: 'Shopping', icon: '🛍️', category: 'GENERAL_MERCHANDISE' },
  { id: 'transportation', label: 'Transportation', icon: '🚗', category: 'TRANSPORTATION' },
  { id: 'rent_bills', label: 'Rent & Bills', icon: '🏠', category: 'RENT_AND_UTILITIES' },
  { id: 'personal_care', label: 'Personal Care', icon: '💅', category: 'PERSONAL_CARE' },
  { id: 'travel', label: 'Travel', icon: '✈️', category: 'TRAVEL' },
  { id: 'medical', label: 'Medical', icon: '🏥', category: 'MEDICAL' },
  { id: 'services', label: 'Services', icon: '🔧', category: 'GENERAL_SERVICES' },
  { id: 'subscriptions', label: 'Subscriptions', icon: '📱', category: 'SUBSCRIPTIONS' },
  { id: 'vehicle_payment', label: 'Vehicle / Auto', icon: '🚙', category: 'VEHICLE_AUTO' },
  { id: 'debt_payment', label: 'Loan / Debt', icon: '💳', category: 'LOAN_DEBT_PAYMENT' },
  { id: 'insurance', label: 'Insurance', icon: '🛡️', category: 'INSURANCE' },
  { id: 'investment', label: 'Investment', icon: '📈', category: 'INVESTMENTS' },
  { id: 'other', label: 'Other', icon: '📦', category: 'OTHER' },
];

const BUSINESS_OPTIONS = [
  { id: 'business_cogs', label: 'Business (COGS)', icon: '📊', classification: 'business_cogs' },
  { id: 'business_operations', label: 'Business Operations', icon: '💼', classification: 'business_operations' },
  { id: 'payroll_contractors', label: 'Payroll & Contractors', icon: '👥', category: 'PAYROLL_CONTRACTORS', classification: 'payroll_contractors' },
  { id: 'business_taxes', label: 'Business Taxes', icon: '📋', category: 'BUSINESS_TAXES', classification: 'business_taxes' },
];

// Helper to build classification data
function buildClassificationData(optionId, isBusinessClassification = false) {
  const allOptions = [...CATEGORY_OPTIONS, ...BUSINESS_OPTIONS];
  const opt = allOptions.find(o => o.id === optionId);
  if (!opt) return { classification: optionId, type: 'personal' };
  
  // Determine type based on classification
  let type = 'personal';  // default
  const classification = opt.classification || opt.id;
  
  // Business classifications
  if (classification === 'business_cogs' || classification === 'business_operations' || 
      classification === 'business_taxes' || classification === 'payroll_contractors') {
    type = 'business';
  }
  
  const data = { type, reclassified_at: new Date().toISOString() };
  
  if (opt.category) {
    data.category_override = opt.category;
    data.classification = classification;
  } else {
    data.classification = classification;
  }
  
  return data;
}

/* ── Swipe Hint Overlay ── */
function SwipeHints({ onDismiss }) {
  return (
    <div 
      data-testid="swipe-hints-overlay"
      className="fade-in"
      onClick={onDismiss}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        cursor: 'pointer',
      }}
    >
      <Mo style={{ fontSize: 10, color: GOLD, letterSpacing: 2, marginBottom: 24 }}>SWIPE GUIDE</Mo>
      
      {/* Right - Personal */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#22c55e20', border: '2px solid #22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </div>
        <div>
          <Bd style={{ fontSize: 16, fontWeight: 700, color: '#22c55e', display: 'block' }}>SWIPE RIGHT</Bd>
          <Mo style={{ fontSize: 12, color: 'var(--mute)' }}>Personal Expense</Mo>
        </div>
      </div>
      
      {/* Left - Business */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#3b82f620', border: '2px solid #3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </div>
        <div>
          <Bd style={{ fontSize: 16, fontWeight: 700, color: '#3b82f6', display: 'block' }}>SWIPE LEFT</Bd>
          <Mo style={{ fontSize: 12, color: 'var(--mute)' }}>Business Expense</Mo>
        </div>
      </div>
      
      {/* Up - Ignore */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: `${EMBER}20`, border: `2px solid ${EMBER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke={EMBER} strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </div>
        <div>
          <Bd style={{ fontSize: 16, fontWeight: 700, color: EMBER, display: 'block' }}>SWIPE UP</Bd>
          <Mo style={{ fontSize: 12, color: 'var(--mute)' }}>Ignore / Not Spending</Mo>
        </div>
      </div>
      
      {/* Tap */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: `${GOLD}20`, border: `2px solid ${GOLD}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
          </svg>
        </div>
        <div>
          <Bd style={{ fontSize: 16, fontWeight: 700, color: GOLD, display: 'block' }}>TAP CARD</Bd>
          <Mo style={{ fontSize: 12, color: 'var(--mute)' }}>More categories</Mo>
        </div>
      </div>
      
      <Mo style={{ fontSize: 11, color: 'var(--mute)', marginTop: 32 }}>Tap anywhere to continue</Mo>
    </div>
  );
}

/* ── Category Picker Modal ── */
function CategoryPicker({ onSelect, onClose, currentCategory }) {
  return (
    <div 
      data-testid="category-picker-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div 
        onClick={e => e.stopPropagation()}
        className="fade-in"
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '70vh',
          background: '#1a1a1a',
          borderRadius: '20px 20px 0 0',
          padding: '24px 20px',
          overflowY: 'auto',
        }}
      >
        <Mo style={{ fontSize: 10, color: GOLD, letterSpacing: 2, display: 'block', marginBottom: 16 }}>SELECT CATEGORY</Mo>
        
        {/* Personal Categories */}
        <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 8 }}>PERSONAL</Mo>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
          {CATEGORY_OPTIONS.map(opt => (
            <button
              key={opt.id}
              data-testid={`category-option-${opt.id}`}
              onClick={() => onSelect(opt.id, false)}
              style={{
                padding: '12px 8px',
                background: currentCategory === opt.category ? `${GOLD}20` : 'var(--surface)',
                border: currentCategory === opt.category ? `1px solid ${GOLD}` : '1px solid var(--ghost)',
                borderRadius: 10,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 20 }}>{opt.icon}</span>
              <Mo style={{ fontSize: 9, color: currentCategory === opt.category ? GOLD : 'var(--light)', textAlign: 'center' }}>{opt.label}</Mo>
            </button>
          ))}
        </div>
        
        {/* Business Categories */}
        <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 8 }}>BUSINESS</Mo>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {BUSINESS_OPTIONS.map(opt => (
            <button
              key={opt.id}
              data-testid={`category-option-${opt.id}`}
              onClick={() => onSelect(opt.id, true)}
              style={{
                padding: '12px 8px',
                background: 'var(--surface)',
                border: '1px solid var(--ghost)',
                borderRadius: 10,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 20 }}>{opt.icon}</span>
              <Mo style={{ fontSize: 9, color: 'var(--light)', textAlign: 'center' }}>{opt.label}</Mo>
            </button>
          ))}
        </div>
        
        <button
          onClick={onClose}
          style={{
            width: '100%',
            marginTop: 20,
            padding: '14px',
            background: 'transparent',
            border: '1px solid var(--ghost)',
            borderRadius: 10,
            color: 'var(--mute)',
            fontFamily: MONO,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >CANCEL</button>
      </div>
    </div>
  );
}

/* ── Merchant Rule Confirmation Dialog ── */
function MerchantRuleDialog({ merchant, txnCount, onApplyAll, onJustOne, onCancel }) {
  return (
    <div 
      data-testid="quickfix-merchant-dialog"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={onCancel}
    >
      <div 
        onClick={e => e.stopPropagation()}
        className="fade-in"
        style={{
          background: '#1a1a1a',
          borderRadius: 16,
          padding: 28,
          border: '1px solid rgba(255,255,255,0.12)',
          maxWidth: 380,
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        }}
      >
        <Mo style={{ fontSize: 9, color: GOLD, letterSpacing: 2, display: 'block', marginBottom: 12 }}>MERCHANT RULE</Mo>
        <Bd style={{ fontSize: 15, color: 'var(--light)', display: 'block', lineHeight: 1.5, marginBottom: 6 }}>
          Apply to all transactions from
        </Bd>
        <Bd style={{ fontSize: 15, color: GOLD, fontWeight: 700, display: 'block', marginBottom: 6 }}>
          {merchant}?
        </Bd>
        <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginBottom: 20, lineHeight: 1.4 }}>
          {txnCount} transactions from this merchant will be affected.
        </Mo>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            data-testid="quickfix-apply-all-btn"
            onClick={onApplyAll}
            style={{
              padding: '14px 18px',
              background: GOLD,
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              fontFamily: MONO,
              fontSize: 12,
              fontWeight: 700,
              color: '#111',
              letterSpacing: 0.5,
            }}
          >APPLY TO ALL ({txnCount})</button>
          <button
            data-testid="quickfix-just-one-btn"
            onClick={onJustOne}
            style={{
              padding: '14px 18px',
              background: 'var(--surface)',
              border: '1px solid var(--ghost)',
              borderRadius: 10,
              cursor: 'pointer',
              fontFamily: MONO,
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--light)',
              letterSpacing: 0.5,
            }}
          >JUST THIS ONE</button>
          <button
            onClick={onCancel}
            style={{
              padding: '12px 16px',
              background: 'transparent',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              fontFamily: MONO,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--mute)',
              letterSpacing: 0.5,
            }}
          >CANCEL</button>
        </div>
      </div>
    </div>
  );
}

/* ── Transaction Card with Action Buttons ── */
function TransactionCard({ transaction, onAction }) {
  const categoryLabel = CATEGORY_LABELS[transaction.plaid_category] || transaction.plaid_category || 'Uncategorized';

  return (
    <div data-testid="quickfix-card" style={{
      width: '100%',
      maxWidth: 360,
      background: '#1a1a1a',
      border: '2px solid var(--ghost)',
      borderRadius: 20,
      padding: 24,
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    }}>
      {/* Merchant Name */}
      <Dp style={{ fontSize: 20, fontWeight: 800, color: 'var(--light)', display: 'block' }}>
        {transaction.merchant}
      </Dp>

      {/* Amount */}
      <Dp style={{ fontSize: 36, fontWeight: 800, color: GOLD, display: 'block' }}>
        ${transaction.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </Dp>

      {/* Date */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--mute)" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <Mo style={{ fontSize: 12, color: 'var(--mute)' }}>
          {new Date(transaction.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </Mo>
      </div>

      {/* Current Category */}
      <div style={{ padding: '10px 14px', background: 'var(--surface)', borderRadius: 10 }}>
        <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 4 }}>CURRENT CATEGORY</Mo>
        <Bd style={{ fontSize: 13, color: 'var(--light)' }}>{categoryLabel}</Bd>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 4 }}>
        <button
          data-testid="quickfix-business-btn"
          onClick={() => onAction('business')}
          style={{
            padding: '14px 8px',
            background: '#3b82f610',
            border: '1.5px solid #3b82f6',
            borderRadius: 12,
            cursor: 'pointer',
            fontFamily: MONO,
            fontSize: 11,
            fontWeight: 700,
            color: '#3b82f6',
            letterSpacing: 0.5,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.target.style.background = '#3b82f625'}
          onMouseLeave={e => e.target.style.background = '#3b82f610'}
        >BUSINESS</button>
        <button
          data-testid="quickfix-ignore-btn"
          onClick={() => onAction('ignore')}
          style={{
            padding: '14px 8px',
            background: `${EMBER}10`,
            border: `1.5px solid ${EMBER}`,
            borderRadius: 12,
            cursor: 'pointer',
            fontFamily: MONO,
            fontSize: 11,
            fontWeight: 700,
            color: EMBER,
            letterSpacing: 0.5,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.target.style.background = `${EMBER}25`}
          onMouseLeave={e => e.target.style.background = `${EMBER}10`}
        >IGNORE</button>
        <button
          data-testid="quickfix-personal-btn"
          onClick={() => onAction('personal')}
          style={{
            padding: '14px 8px',
            background: '#22c55e10',
            border: '1.5px solid #22c55e',
            borderRadius: 12,
            cursor: 'pointer',
            fontFamily: MONO,
            fontSize: 11,
            fontWeight: 700,
            color: '#22c55e',
            letterSpacing: 0.5,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.target.style.background = '#22c55e25'}
          onMouseLeave={e => e.target.style.background = '#22c55e10'}
        >PERSONAL</button>
      </div>
    </div>
  );
}

/* ── Summary Screen ── */
function QuickFixSummary({ changes, onDone, onReAnalyze, reAnalyzing }) {
  const personalCount = changes.filter(c => c.classification === 'personal').length;
  const businessCount = changes.filter(c => c.classification === 'business').length;
  const ignoreCount = changes.filter(c => c.classification === 'ignore').length;
  const customCount = changes.filter(c => !['personal', 'business', 'ignore'].includes(c.classification)).length;
  
  return (
    <div data-testid="quickfix-summary" className="fade-in" style={{ padding: 24, textAlign: 'center' }}>
      <div style={{ 
        width: 80, 
        height: 80, 
        borderRadius: '50%', 
        background: `${GOLD}20`, 
        border: `3px solid ${GOLD}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
      }}>
        <svg width={36} height={36} viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2.5" strokeLinecap="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>
      
      <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
        Quick Fix Complete!
      </Dp>
      <Mo style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 32 }}>
        {changes.length} transaction{changes.length !== 1 ? 's' : ''} categorized
      </Mo>
      
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 32 }}>
        {personalCount > 0 && (
          <div style={{ padding: 16, background: '#22c55e10', border: '1px solid #22c55e30', borderRadius: 12 }}>
            <Dp style={{ fontSize: 28, fontWeight: 800, color: '#22c55e' }}>{personalCount}</Dp>
            <Mo style={{ fontSize: 10, color: '#22c55e' }}>Personal</Mo>
          </div>
        )}
        {businessCount > 0 && (
          <div style={{ padding: 16, background: '#3b82f610', border: '1px solid #3b82f630', borderRadius: 12 }}>
            <Dp style={{ fontSize: 28, fontWeight: 800, color: '#3b82f6' }}>{businessCount}</Dp>
            <Mo style={{ fontSize: 10, color: '#3b82f6' }}>Business</Mo>
          </div>
        )}
        {ignoreCount > 0 && (
          <div style={{ padding: 16, background: `${EMBER}10`, border: `1px solid ${EMBER}30`, borderRadius: 12 }}>
            <Dp style={{ fontSize: 28, fontWeight: 800, color: EMBER }}>{ignoreCount}</Dp>
            <Mo style={{ fontSize: 10, color: EMBER }}>Ignored</Mo>
          </div>
        )}
        {customCount > 0 && (
          <div style={{ padding: 16, background: `${GOLD}10`, border: `1px solid ${GOLD}30`, borderRadius: 12 }}>
            <Dp style={{ fontSize: 28, fontWeight: 800, color: GOLD }}>{customCount}</Dp>
            <Mo style={{ fontSize: 10, color: GOLD }}>Custom</Mo>
          </div>
        )}
      </div>
      
      {/* Actions */}
      <button
        data-testid="quickfix-reanalyze-btn"
        onClick={onReAnalyze}
        disabled={reAnalyzing}
        style={{
          width: '100%',
          padding: '16px 24px',
          background: GOLD,
          border: 'none',
          borderRadius: 12,
          cursor: reAnalyzing ? 'wait' : 'pointer',
          fontFamily: MONO,
          fontSize: 13,
          fontWeight: 700,
          color: '#111',
          letterSpacing: 0.5,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          opacity: reAnalyzing ? 0.7 : 1,
        }}
      >
        {reAnalyzing ? (
          <>
            <div style={{ width: 16, height: 16, border: '2px solid #000', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            RE-ANALYZING...
          </>
        ) : (
          <>
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round">
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
            RE-ANALYZE NOW
          </>
        )}
      </button>
      
      <button
        data-testid="quickfix-done-btn"
        onClick={onDone}
        style={{
          width: '100%',
          padding: '14px 24px',
          background: 'transparent',
          border: '1px solid var(--ghost)',
          borderRadius: 12,
          cursor: 'pointer',
          fontFamily: MONO,
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--mute)',
          letterSpacing: 0.5,
        }}
      >DONE</button>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN QUICK FIX COMPONENT
   ══════════════════════════════════════════════ */
export function QuickFixScreen({ analysisData, existingClassifications, onClose, onReAnalyze }) {
  const [transactions, setTransactions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [merchantDialog, setMerchantDialog] = useState(null);
  const [pendingClassification, setPendingClassification] = useState(null);
  const [changes, setChanges] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [reAnalyzing, setReAnalyzing] = useState(false);
  
  // Extract uncategorized/ambiguous transactions
  useEffect(() => {
    if (!analysisData?.categories) return;

    const ambiguousCategories = ['OTHER', 'GENERAL_MERCHANDISE', 'BANK_FEES', 'TRANSFER', 'LOAN_PAYMENTS'];
    const classifiedMerchants = new Set(Object.keys(existingClassifications || {}));

    const uncategorized = [];
    const seenTxns = new Set();

    for (const cat of analysisData.categories) {
      if (!cat.transactions) continue;

      for (const txn of cat.transactions) {
        const txnKey = `${txn.merchant}-${txn.date}-${txn.amount}`;

        // Skip if already seen or merchant already has a rule
        if (seenTxns.has(txnKey)) continue;
        if (classifiedMerchants.has(txn.merchant)) continue;

        // Check if ambiguous category
        const isAmbiguous = ambiguousCategories.some(c => cat.name?.includes(c));

        if (isAmbiguous) {
          uncategorized.push({
            ...txn,
            plaid_category: cat.name,
            category_display: cat.display_name || CATEGORY_LABELS[cat.name] || cat.name,
          });
          seenTxns.add(txnKey);
        }
      }
    }

    setTransactions(uncategorized);

    // Check if no uncategorized, show completion immediately
    if (uncategorized.length === 0) {
      setIsComplete(true);
    }
  }, [analysisData, existingClassifications]);
  
  // Count transactions by merchant for dialog
  const countMerchantTransactions = (merchantName) => {
    let count = 0;
    for (const cat of analysisData?.categories || []) {
      if (cat.transactions) {
        count += cat.transactions.filter(t => t.merchant === merchantName).length;
      }
    }
    return count;
  };
  
  // Handle button action classification
  const handleAction = (direction) => {
    const txn = transactions[currentIndex];
    if (!txn) return;

    let classification;
    let optionId;

    switch (direction) {
      case 'personal':
        classification = 'personal';
        optionId = 'other';
        break;
      case 'business':
        classification = 'business_operations';
        optionId = 'business_operations';
        break;
      case 'ignore':
        classification = 'ignore';
        optionId = 'ignore';
        break;
      default:
        return;
    }

    const txnCount = countMerchantTransactions(txn.merchant);

    if (txnCount > 1) {
      setMerchantDialog({ merchant: txn.merchant, txnCount });
      setPendingClassification({ classification, optionId });
    } else {
      applyClassification(txn, classification, optionId, true);
    }
  };
  
  // Handle category picker selection
  const handleCategorySelect = (optionId, isBusiness) => {
    const txn = transactions[currentIndex];
    if (!txn) return;
    
    const classification = isBusiness ? optionId : 'personal';
    const txnCount = countMerchantTransactions(txn.merchant);
    
    setShowCategoryPicker(false);
    
    if (txnCount > 1) {
      setMerchantDialog({ merchant: txn.merchant, txnCount });
      setPendingClassification({ classification, optionId });
    } else {
      applyClassification(txn, classification, optionId, true);
    }
  };
  
  // Apply classification (with or without merchant rule)
  const applyClassification = async (txn, classification, optionId, applyAsMerchantRule) => {
    const classData = buildClassificationData(optionId);
    
    // Determine direction: "debit" for spending (amount >= 0), "credit" for income (amount < 0)
    const direction = txn.amount >= 0 ? 'debit' : 'credit';
    classData.direction = direction;
    
    // Build composite key
    const compositeKey = `${txn.merchant}__${direction}`;
    
    // Record the change
    setChanges(prev => [...prev, {
      merchant: txn.merchant,
      classification,
      optionId,
      isMerchantRule: applyAsMerchantRule,
      direction,
    }]);
    
    // Save to backend if merchant rule
    if (applyAsMerchantRule) {
      try {
        const newClassifications = {
          ...existingClassifications,
          [compositeKey]: classData,  // Use composite key instead of merchant name only
        };
        
        await fetch(`${API}/api/insights/merchant-classifications`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ classifications: newClassifications }),
        });
      } catch (err) {
        console.error('Failed to save classification:', err);
      }
    }
    
    // Move to next card or complete
    if (currentIndex >= transactions.length - 1) {
      setIsComplete(true);
    } else {
      setCurrentIndex(prev => prev + 1);
    }
    
    // Reset dialogs
    setMerchantDialog(null);
    setPendingClassification(null);
  };
  
  // Handle "Apply to All" from merchant dialog
  const handleApplyAll = () => {
    const txn = transactions[currentIndex];
    if (!txn || !pendingClassification) return;
    applyClassification(txn, pendingClassification.classification, pendingClassification.optionId, true);
  };
  
  // Handle "Just This One" from merchant dialog
  const handleJustOne = () => {
    const txn = transactions[currentIndex];
    if (!txn || !pendingClassification) return;
    applyClassification(txn, pendingClassification.classification, pendingClassification.optionId, false);
  };
  
  // Handle re-analysis
  const handleReAnalyze = async () => {
    setReAnalyzing(true);
    try {
      if (onReAnalyze) {
        await onReAnalyze();
      }
      onClose();
    } catch (err) {
      console.error('Re-analysis failed:', err);
    } finally {
      setReAnalyzing(false);
    }
  };
  
  const currentTxn = transactions[currentIndex];
  const remaining = transactions.length - currentIndex;
  
  return (
    <div 
      data-testid="quickfix-screen"
      style={{
        position: 'fixed',
        inset: 0,
        background: '#0d0d0d',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{ 
        padding: '16px 20px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid var(--faint)',
      }}>
        <button
          data-testid="quickfix-close-btn"
          onClick={onClose}
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--ghost)',
            borderRadius: 8,
            padding: '8px 14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--mute)',
            fontFamily: MONO,
            fontSize: 11,
          }}
        >
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          CLOSE
        </button>
        
        <div style={{ textAlign: 'center' }}>
          <Mo style={{ fontSize: 10, color: GOLD, letterSpacing: 2, display: 'block' }}>QUICK FIX</Mo>
          {!isComplete && transactions.length > 0 && (
            <Mo data-testid="quickfix-progress" style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 4 }}>
              {remaining} of {transactions.length} remaining
            </Mo>
          )}
        </div>
        
        {!isComplete && (
          <button
            data-testid="quickfix-done-early-btn"
            onClick={() => setIsComplete(true)}
            style={{
              background: 'transparent',
              border: '1px solid var(--ghost)',
              borderRadius: 8,
              padding: '8px 14px',
              cursor: 'pointer',
              color: 'var(--mute)',
              fontFamily: MONO,
              fontSize: 11,
            }}
          >DONE</button>
        )}
        
        {isComplete && <div style={{ width: 80 }} />}
      </div>
      
      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 20px 0', overflow: 'hidden' }}>
        {isComplete ? (
          <QuickFixSummary 
            changes={changes} 
            onDone={onClose} 
            onReAnalyze={handleReAnalyze}
            reAnalyzing={reAnalyzing}
          />
        ) : transactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Dp style={{ fontSize: 18, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
              No transactions to categorize
            </Dp>
            <Mo style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24 }}>
              All transactions are already categorized or have merchant rules.
            </Mo>
            <button
              onClick={onClose}
              style={{
                padding: '14px 28px',
                background: GOLD,
                border: 'none',
                borderRadius: 12,
                cursor: 'pointer',
                fontFamily: MONO,
                fontSize: 12,
                fontWeight: 700,
                color: '#111',
              }}
            >DONE</button>
          </div>
        ) : (
          currentTxn && (
            <TransactionCard
              transaction={currentTxn}
              onAction={handleAction}
            />
          )
        )}
      </div>
      
      {/* Category Picker Modal */}
      {showCategoryPicker && currentTxn && (
        <CategoryPicker
          currentCategory={currentTxn.plaid_category}
          onSelect={handleCategorySelect}
          onClose={() => setShowCategoryPicker(false)}
        />
      )}
      
      {/* Merchant Rule Dialog */}
      {merchantDialog && (
        <MerchantRuleDialog
          merchant={merchantDialog.merchant}
          txnCount={merchantDialog.txnCount}
          onApplyAll={handleApplyAll}
          onJustOne={handleJustOne}
          onCancel={() => {
            setMerchantDialog(null);
            setPendingClassification(null);
          }}
        />
      )}
    </div>
  );
}
